import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import Container from 'react-bootstrap/Container';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import Table from 'react-bootstrap/Table';
import { api } from '../../lib/api';
import FacilitatorPageHeader from './FacilitatorPageHeader';

// Tenant-scoped analytics for educator-admin. Mirrors the platform-admin
// PerformanceStatsView but uses the /api/stats/* endpoints (filtered to
// the caller's tenant by the server).
//
// Tabs: cases | drills | spot-error | outpatient | students | authors

function defaultRange() {
  const today = new Date();
  const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
  return {
    from: monthAgo.toISOString().slice(0, 10),
    to: today.toISOString().slice(0, 10),
  };
}

function formatDate(value) {
  if (!value) return '—';
  try { return new Date(value).toLocaleString('en-GB'); } catch { return '—'; }
}

function downloadCsv(filename, rows) {
  if (!rows.length) return;
  const headers = Object.keys(rows[0]);
  const escape = (val) => {
    if (val === null || val === undefined) return '';
    const str = String(val);
    if (str.includes(',') || str.includes('"') || str.includes('\n')) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  };
  const lines = [
    headers.join(','),
    ...rows.map((row) => headers.map((h) => escape(row[h])).join(',')),
  ];
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// Per-module tab metadata. The "moduleKey" matches the URL segment used by
// the server endpoints (api.getEducatorStatsModule(moduleKey, ...)).
const MODULE_TABS = {
  drills: { label: 'Calc exercises', moduleKey: 'drills', scoreLabel: 'Avg score' },
  'spot-error': { label: 'Spot-the-error', moduleKey: 'spot-error-drills', scoreLabel: 'Avg F1 (%)' },
  outpatient: { label: 'Outpatient', moduleKey: 'outpatient-prescriptions', scoreLabel: 'Avg overall (%)' },
  mcq: { label: 'Question bank', moduleKey: 'mcq-exercises', scoreLabel: 'Avg score' },
  revision: { label: 'Revision exercises', moduleKey: 'revision-exercises', scoreLabel: 'Avg score' },
};

// Display labels for module types in the student drill-down view.
const MODULE_TYPE_LABELS = {
  case: 'Case study',
  drill: 'Calc drill',
  'spot-error': 'Spot-the-error',
  outpatient: 'Outpatient',
};

const MODULE_TYPE_BADGE = {
  case: 'primary',
  drill: 'info',
  'spot-error': 'warning',
  outpatient: 'dark',
};

// Map the moduleKey used by the stats endpoints back to the moduleType used
// by the cross-module review API. Cases sit on a different stats endpoint
// (getEducatorStatsCaseSessions) so they have their own key in this map.
const MODULE_KEY_TO_REVIEW_TYPE = {
  cases: 'case',
  drills: 'drill',
  'spot-error-drills': 'spot-error',
  'outpatient-prescriptions': 'outpatient',
};

const EducatorStatsView = ({ token, onBack }) => {
  const [tab, setTab] = useState('cases');
  const [range, setRange] = useState(defaultRange);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [cases, setCases] = useState([]);
  const [students, setStudents] = useState([]);
  const [authors, setAuthors] = useState([]);
  const [moduleDrills, setModuleDrills] = useState([]);
  const [drillDown, setDrillDown] = useState(null); // { moduleKey, drill, sessions }
  const [studentDrill, setStudentDrill] = useState(null); // { student, sessions }
  // Tracks sessions the lecturer has just flagged so the row shows feedback.
  // We don't refetch after each flag - the lecturer-flag op is idempotent.
  const [flaggedSessionIds, setFlaggedSessionIds] = useState(new Set());
  const [flaggingSessionId, setFlaggingSessionId] = useState(null);

  const handleFlagSession = async (moduleType, sessionId) => {
    if (!moduleType || !sessionId) return;
    setFlaggingSessionId(sessionId);
    try {
      await api.flagSessionForReview(moduleType, sessionId, token);
      setFlaggedSessionIds((prev) => {
        const next = new Set(prev);
        next.add(sessionId);
        return next;
      });
    } catch (err) {
      setError(err?.message || 'Could not flag for review.');
    } finally {
      setFlaggingSessionId(null);
    }
  };

  const refresh = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      if (tab === 'cases') {
        const result = await api.getEducatorStatsCases(token, range);
        setCases(result.cases || []);
      } else if (tab === 'students') {
        const result = await api.getEducatorStatsStudents(token, range);
        setStudents(result.students || []);
      } else if (tab === 'authors') {
        const result = await api.getEducatorStatsAuthors(token, range);
        setAuthors(result.authors || []);
      } else if (MODULE_TABS[tab]) {
        const { moduleKey } = MODULE_TABS[tab];
        const result = await api.getEducatorStatsModule(moduleKey, token, range);
        setModuleDrills(result.drills || []);
      }
    } catch (err) {
      setError(err?.message || 'Failed to load stats.');
    } finally {
      setLoading(false);
    }
  }, [tab, range, token]);

  useEffect(() => { refresh(); }, [refresh]);

  const totals = useMemo(() => {
    if (tab === 'cases') {
      return {
        sessions: cases.reduce((acc, c) => acc + (c.sessionCount || 0), 0),
        completions: cases.reduce((acc, c) => acc + (c.completionCount || 0), 0),
        rows: cases.length,
        rowsLabel: 'cases',
      };
    }
    if (tab === 'students') {
      return {
        sessions: students.reduce((acc, s) => acc + (s.sessionCount || 0), 0),
        completions: students.reduce((acc, s) => acc + (s.completionCount || 0), 0),
        rows: students.length,
        rowsLabel: 'students',
      };
    }
    if (tab === 'authors') {
      return {
        sessions: authors.reduce((acc, a) => acc + (a.sessionCount || 0), 0),
        completions: authors.reduce((acc, a) => acc + (a.completionCount || 0), 0),
        rows: authors.length,
        rowsLabel: 'authors',
      };
    }
    return {
      sessions: moduleDrills.reduce((acc, d) => acc + (d.sessionCount || 0), 0),
      completions: moduleDrills.reduce((acc, d) => acc + (d.completionCount || 0), 0),
      rows: moduleDrills.length,
      rowsLabel: 'drills',
    };
  }, [tab, cases, students, authors, moduleDrills]);

  const handleExport = () => {
    const filenameBase = `tenant-${tab}-${range.from}-to-${range.to}.csv`;
    if (tab === 'cases') {
      downloadCsv(filenameBase, cases.map((c) => ({
        title: c.title, owner_email: c.ownerEmail, owner_name: c.ownerDisplayName,
        status: c.status, student_access_enabled: c.studentAccessEnabled,
        sessions: c.sessionCount, completions: c.completionCount,
        avg_score_pct: c.avgScore ?? '', last_activity_at: c.lastActivityAt || '',
      })));
    } else if (tab === 'students') {
      downloadCsv(filenameBase, students.map((s) => ({
        email: s.email, name: s.displayName, role: s.role,
        sessions: s.sessionCount, completions: s.completionCount,
        avg_score: s.avgScore ?? '',
        last_login_at: s.lastLoginAt || '', last_activity_at: s.lastActivityAt || '',
      })));
    } else if (tab === 'authors') {
      downloadCsv(filenameBase, authors.map((a) => ({
        email: a.email, name: a.displayName, role: a.role,
        cases_owned: a.casesOwned, sessions: a.sessionCount,
        completions: a.completionCount, last_published_at: a.lastPublishedAt || '',
      })));
    } else {
      downloadCsv(filenameBase, moduleDrills.map((d) => ({
        title: d.title, status: d.status, difficulty: d.difficulty,
        owner_email: d.ownerEmail, owner_name: d.ownerDisplayName,
        sessions: d.sessionCount, completions: d.completionCount,
        avg_score: d.avgScore ?? '', last_activity_at: d.lastActivityAt || '',
      })));
    }
  };

  const openCaseDrill = async (caseRow) => {
    setLoading(true); setError('');
    try {
      const result = await api.getEducatorStatsCaseSessions(caseRow.id, token, range);
      setDrillDown({ moduleKey: 'cases', drill: caseRow, sessions: result.sessions || [], scoreLabel: 'Score' });
    } catch (err) {
      setError(err?.message || 'Failed to load drill-down.');
    } finally {
      setLoading(false);
    }
  };

  const openStudentDrill = async (studentRow) => {
    setLoading(true); setError('');
    try {
      const result = await api.getEducatorStatsStudentSessions(studentRow.id, token, range);
      setStudentDrill({ student: result.student || studentRow, sessions: result.sessions || [] });
    } catch (err) {
      setError(err?.message || 'Failed to load student drill-down.');
    } finally {
      setLoading(false);
    }
  };

  const openModuleDrill = async (drillRow) => {
    if (!MODULE_TABS[tab]) return;
    const { moduleKey, scoreLabel } = MODULE_TABS[tab];
    setLoading(true); setError('');
    try {
      const result = await api.getEducatorStatsModuleDrillSessions(moduleKey, drillRow.id, token, range);
      setDrillDown({ moduleKey, drill: drillRow, sessions: result.sessions || [], scoreLabel });
    } catch (err) {
      setError(err?.message || 'Failed to load drill-down.');
    } finally {
      setLoading(false);
    }
  };

  if (studentDrill) {
    // Group sessions by module type for the per-module summary card.
    const summary = studentDrill.sessions.reduce((acc, s) => {
      const t = s.moduleType;
      if (!acc[t]) acc[t] = { total: 0, completed: 0, scoreSum: 0, scoreCount: 0 };
      acc[t].total += 1;
      if (s.status === 'completed') {
        acc[t].completed += 1;
        if (s.score !== null && s.score !== undefined) {
          acc[t].scoreSum += s.score;
          acc[t].scoreCount += 1;
        }
      }
      return acc;
    }, {});
    return (
      <div className="student-page">
        <Container className="mt-4">
          <FacilitatorPageHeader
            title={<span className="text-truncate d-inline-block" style={{ maxWidth: '100%' }}>
              {studentDrill.student.displayName || studentDrill.student.email}
            </span>}
            description={`${studentDrill.student.email} · ${studentDrill.sessions.length} session${studentDrill.sessions.length === 1 ? '' : 's'} in selected period`}
            onBack={() => setStudentDrill(null)}
            backLabel="Back to students"
          />

          <div className="card mb-3">
            <div className="card-body">
              <h6 className="mb-2">By module type</h6>
              {Object.keys(summary).length === 0 ? (
                <p className="text-muted mb-0">No sessions in this period.</p>
              ) : (
                <div className="d-flex flex-wrap gap-3">
                  {Object.entries(summary).map(([moduleType, s]) => (
                    <div key={moduleType} className="border rounded p-2" style={{ minWidth: 180 }}>
                      <Badge bg={MODULE_TYPE_BADGE[moduleType] || 'secondary'} className="mb-1">{MODULE_TYPE_LABELS[moduleType] || moduleType}</Badge>
                      <div className="small text-muted">{s.total} session{s.total === 1 ? '' : 's'}, {s.completed} completed</div>
                      <div className="small">Avg score: {s.scoreCount ? `${(s.scoreSum / s.scoreCount).toFixed(1)}` : '—'}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="card">
            <div className="card-body">
              <h6 className="mb-2">All sessions (most recent first)</h6>
              {studentDrill.sessions.length === 0 ? (
                <p className="text-muted mb-0">No sessions in this period.</p>
              ) : (
                <Table responsive hover size="sm" className="mb-0">
                  <thead>
                    <tr>
                      <th>Module</th>
                      <th>Item</th>
                      <th>Status</th>
                      <th>Score</th>
                      <th>Attempt</th>
                      <th>Started</th>
                      <th>Completed</th>
                      <th className="text-end">Review</th>
                    </tr>
                  </thead>
                  <tbody>
                    {studentDrill.sessions.map((s) => {
                      const canFlag = s.status === 'completed';
                      const flagged = flaggedSessionIds.has(s.id);
                      return (
                        <tr key={s.id}>
                          <td><Badge bg={MODULE_TYPE_BADGE[s.moduleType] || 'secondary'}>{MODULE_TYPE_LABELS[s.moduleType] || s.moduleType}</Badge></td>
                          <td className="small">{s.sourceTitle}</td>
                          <td>
                            {s.status === 'completed' ? <Badge bg="success">Completed</Badge>
                              : s.status === 'in_progress' ? <Badge bg="info">In progress</Badge>
                              : <Badge bg="secondary">{s.status}</Badge>}
                          </td>
                          <td>{s.score !== null && s.score !== undefined ? s.score : '—'}</td>
                          <td className="small text-muted">#{s.attemptNumber}</td>
                          <td className="small text-muted">{formatDate(s.startedAt)}</td>
                          <td className="small text-muted">{s.completedAt ? formatDate(s.completedAt) : '—'}</td>
                          <td className="text-end">
                            {canFlag ? (
                              flagged ? (
                                <Badge bg="success"><i className="bi bi-flag-fill me-1" />Flagged</Badge>
                              ) : (
                                <Button
                                  size="sm"
                                  variant="outline-dark"
                                  disabled={flaggingSessionId === s.id}
                                  onClick={() => handleFlagSession(s.moduleType, s.id)}
                                >
                                  <i className="bi bi-flag me-1" />Flag
                                </Button>
                              )
                            ) : null}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              )}
            </div>
          </div>
        </Container>
      </div>
    );
  }

  if (drillDown) {
    return (
      <div className="student-page">
        <Container className="mt-4">
          <FacilitatorPageHeader
            title={<span className="text-truncate d-inline-block" style={{ maxWidth: '100%' }}>
              Sessions — {drillDown.drill.title}
            </span>}
            description={`${drillDown.sessions.length} session${drillDown.sessions.length === 1 ? '' : 's'} in selected period`}
            onBack={() => setDrillDown(null)}
            backLabel="Back to list"
          />
          <div className="card">
            <div className="card-body">
              {drillDown.sessions.length === 0 ? (
                <p className="text-muted mb-0">No sessions for this item in the selected period.</p>
              ) : (
                <Table responsive hover size="sm" className="mb-0">
                  <thead>
                    <tr>
                      <th>Student</th>
                      <th>Status</th>
                      <th>{drillDown.scoreLabel}</th>
                      <th>Started</th>
                      <th>Completed</th>
                      <th className="text-end">Review</th>
                    </tr>
                  </thead>
                  <tbody>
                    {drillDown.sessions.map((s) => {
                      const reviewModuleType = MODULE_KEY_TO_REVIEW_TYPE[drillDown.moduleKey];
                      const canFlag = s.status === 'completed' && reviewModuleType;
                      const flagged = flaggedSessionIds.has(s.id);
                      return (
                        <tr key={s.id}>
                          <td>
                            <div>{s.userDisplayName || s.userEmail || s.userId}</div>
                            {s.userEmail ? <div className="small text-muted">{s.userEmail}</div> : null}
                          </td>
                          <td>
                            {s.status === 'completed' ? <Badge bg="success">Completed</Badge>
                              : s.status === 'in_progress' ? <Badge bg="info">In progress</Badge>
                              : <Badge bg="secondary">{s.status}</Badge>}
                          </td>
                          <td>{s.score !== null && s.score !== undefined ? `${s.score}` : '—'}</td>
                          <td className="small text-muted">{formatDate(s.startedAt)}</td>
                          <td className="small text-muted">{s.completedAt ? formatDate(s.completedAt) : '—'}</td>
                          <td className="text-end">
                            {canFlag ? (
                              flagged ? (
                                <Badge bg="success"><i className="bi bi-flag-fill me-1" />Flagged</Badge>
                              ) : (
                                <Button
                                  size="sm"
                                  variant="outline-dark"
                                  disabled={flaggingSessionId === s.id}
                                  onClick={() => handleFlagSession(reviewModuleType, s.id)}
                                >
                                  <i className="bi bi-flag me-1" />Flag for review
                                </Button>
                              )
                            ) : null}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              )}
            </div>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="student-page">
      <Container className="mt-4">
        <FacilitatorPageHeader
          title="Cohort analytics"
          description="Engagement + outcomes across your tenant. Switch tabs for each module type, students, or authors. Click any case or exercise row to see its session list."
          onBack={onBack}
        />

        <div className="card mb-3">
          <div className="card-body d-flex flex-wrap align-items-center gap-3">
            <div className="d-flex flex-wrap gap-2">
              {[
                { key: 'cases', label: 'Inpatient cases' },
                { key: 'outpatient', label: 'Outpatient cases' },
                { key: 'drills', label: 'Calc drills' },
                { key: 'mcq', label: 'Question bank' },
                { key: 'revision', label: 'Revision' },
                { key: 'spot-error', label: 'Spot-the-error' },
                { key: 'students', label: 'Students' },
                { key: 'authors', label: 'Authors' },
              ].map((t) => (
                <Button
                  key={t.key}
                  size="sm"
                  variant={tab === t.key ? 'primary' : 'outline-secondary'}
                  onClick={() => setTab(t.key)}
                >
                  {t.label}
                </Button>
              ))}
            </div>
            <div className="d-flex align-items-center gap-2 ms-auto">
              <Form.Label className="mb-0 small text-muted">From</Form.Label>
              <Form.Control type="date" size="sm" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })} style={{ width: 150 }} />
              <Form.Label className="mb-0 small text-muted">To</Form.Label>
              <Form.Control type="date" size="sm" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} style={{ width: 150 }} />
              <Button size="sm" variant="success" onClick={handleExport} disabled={totals.rows === 0}>
                <i className="bi bi-download me-1" /> CSV
              </Button>
            </div>
          </div>
        </div>

        {error ? <Alert variant="danger">{error}</Alert> : null}

        <div className="card mb-3">
          <div className="card-body py-2">
            <div className="text-muted small">
              <strong>{totals.sessions}</strong> sessions, <strong>{totals.completions}</strong> completions across <strong>{totals.rows}</strong> {totals.rowsLabel} in this period.
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            {loading ? (
              <div className="text-center py-4"><Spinner animation="border" size="sm" /> Loading…</div>
            ) : tab === 'cases' ? (
              cases.length === 0 ? (
                <p className="text-muted mb-0">No cases with activity in this period.</p>
              ) : (
                <Table responsive hover size="sm" className="mb-0">
                  <thead>
                    <tr>
                      <th>Title</th><th>Owner</th><th>Status</th>
                      <th>Sessions</th><th>Completions</th>
                      <th>Avg score</th><th>Last activity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cases.map((c) => (
                      <tr key={c.id}>
                        <td>
                          <Button variant="link" size="sm" className="p-0 text-start" onClick={() => openCaseDrill(c)}>{c.title}</Button>
                        </td>
                        <td>
                          <div className="small">{c.ownerDisplayName || c.ownerEmail || '—'}</div>
                          {c.ownerEmail && c.ownerDisplayName ? <div className="small text-muted">{c.ownerEmail}</div> : null}
                        </td>
                        <td>
                          {c.status === 'published' ? <Badge bg="success">Published</Badge>
                            : c.status === 'archived' ? <Badge bg="secondary">Archived</Badge>
                            : <Badge bg="warning" text="dark">Draft</Badge>}
                        </td>
                        <td>{c.sessionCount}</td>
                        <td>{c.completionCount}</td>
                        <td>{c.avgScore !== null ? `${c.avgScore}%` : '—'}</td>
                        <td className="small text-muted">{formatDate(c.lastActivityAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )
            ) : MODULE_TABS[tab] ? (
              moduleDrills.length === 0 ? (
                <p className="text-muted mb-0">No exercises with activity in this period.</p>
              ) : (
                <Table responsive hover size="sm" className="mb-0">
                  <thead>
                    <tr>
                      <th>Title</th><th>Owner</th><th>Status</th><th>Difficulty</th>
                      <th>Sessions</th><th>Completions</th>
                      <th>{MODULE_TABS[tab].scoreLabel}</th><th>Last activity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {moduleDrills.map((d) => (
                      <tr key={d.id}>
                        <td>
                          <Button variant="link" size="sm" className="p-0 text-start" onClick={() => openModuleDrill(d)}>{d.title}</Button>
                          {d.summary ? <div className="small text-muted">{d.summary}</div> : null}
                        </td>
                        <td>
                          <div className="small">{d.ownerDisplayName || d.ownerEmail || '—'}</div>
                        </td>
                        <td>
                          {d.status === 'published' ? <Badge bg="success">Published</Badge>
                            : d.status === 'archived' ? <Badge bg="secondary">Archived</Badge>
                            : <Badge bg="warning" text="dark">Draft</Badge>}
                        </td>
                        <td><Badge bg="light" text="dark">{d.difficulty}</Badge></td>
                        <td>{d.sessionCount}</td>
                        <td>{d.completionCount}</td>
                        <td>{d.avgScore !== null && d.avgScore !== undefined ? `${d.avgScore}` : '—'}</td>
                        <td className="small text-muted">{formatDate(d.lastActivityAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )
            ) : tab === 'students' ? (
              students.length === 0 ? (
                <p className="text-muted mb-0">No students yet in this tenant.</p>
              ) : (
                <Table responsive hover size="sm" className="mb-0">
                  <thead>
                    <tr>
                      <th>Student</th>
                      <th>Sessions <span className="text-muted small">(all modules)</span></th>
                      <th>Completions</th>
                      <th>Avg score</th>
                      <th>Last login</th>
                      <th>Last activity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {students.map((s) => (
                      <tr key={s.id}>
                        <td>
                          <Button variant="link" size="sm" className="p-0 text-start" onClick={() => openStudentDrill(s)}>
                            {s.displayName || s.email}
                          </Button>
                          {s.displayName ? <div className="small text-muted">{s.email}</div> : null}
                        </td>
                        <td>{s.sessionCount}</td>
                        <td>{s.completionCount}</td>
                        <td>{s.avgScore !== null ? `${s.avgScore}` : '—'}</td>
                        <td className="small text-muted">{formatDate(s.lastLoginAt)}</td>
                        <td className="small text-muted">{formatDate(s.lastActivityAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )
            ) : (
              authors.length === 0 ? (
                <p className="text-muted mb-0">No authors with published cases in this tenant.</p>
              ) : (
                <Table responsive hover size="sm" className="mb-0">
                  <thead>
                    <tr>
                      <th>Author</th><th>Role</th><th>Cases owned</th>
                      <th>Sessions</th><th>Completions</th><th>Last published</th>
                    </tr>
                  </thead>
                  <tbody>
                    {authors.map((a) => (
                      <tr key={a.id}>
                        <td>
                          <div>{a.displayName || a.email}</div>
                          {a.displayName ? <div className="small text-muted">{a.email}</div> : null}
                        </td>
                        <td><Badge bg="light" text="dark">{a.role}</Badge></td>
                        <td>{a.casesOwned}</td>
                        <td>{a.sessionCount}</td>
                        <td>{a.completionCount}</td>
                        <td className="small text-muted">{formatDate(a.lastPublishedAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )
            )}
          </div>
        </div>
      </Container>
    </div>
  );
};

export default EducatorStatsView;
